/**
 * API service for Fast Cortex
 * Includes error handling, retry logic, and request/response interceptors
 */

import { API_BASE_URL } from '../config/env'

// Configuration
const MAX_RETRIES = 3
const RETRY_DELAY = 1000 // Start with 1 second
const REQUEST_TIMEOUT = 30000 // 30 seconds

// Helper function to get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  }
}

// Helper function to create AbortController with timeout
const createAbortController = (timeout = REQUEST_TIMEOUT) => {
  const controller = new AbortController()
  setTimeout(() => controller.abort(), timeout)
  return controller
}

// Sleep helper for retry delays
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

// Retry logic with exponential backoff
const fetchWithRetry = async (url, options = {}, retries = MAX_RETRIES) => {
  const controller = createAbortController()

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    })

    // If response is ok or it's a client error (4xx), return it
    if (response.ok || (response.status >= 400 && response.status < 500)) {
      return response
    }

    // For server errors (5xx), retry
    if (response.status >= 500 && retries > 0) {
      const delay = RETRY_DELAY * (MAX_RETRIES - retries + 1)
      console.warn(`Request failed with ${response.status}, retrying in ${delay}ms...`)
      await sleep(delay)
      return fetchWithRetry(url, options, retries - 1)
    }

    return response
  } catch (error) {
    // Handle network errors and timeouts
    if (error.name === 'AbortError') {
      if (retries > 0) {
        console.warn('Request timed out, retrying...')
        await sleep(RETRY_DELAY)
        return fetchWithRetry(url, options, retries - 1)
      }
      throw new Error('Request timed out after multiple attempts')
    }

    // For other network errors, retry
    if (retries > 0) {
      const delay = RETRY_DELAY * (MAX_RETRIES - retries + 1)
      console.warn(`Network error: ${error.message}, retrying in ${delay}ms...`)
      await sleep(delay)
      return fetchWithRetry(url, options, retries - 1)
    }

    throw error
  }
}

// Handle API responses with better error handling
const handleResponse = async (response) => {
  // Log response for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`API Response: ${response.url} - Status: ${response.status}`)
  }

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`
    let errorDetail = null

    try {
      const errorData = await response.json()
      errorDetail = errorData.detail || errorData.message || errorMessage
    } catch (e) {
      // If response is not JSON, try to get text
      try {
        errorDetail = await response.text()
      } catch (textError) {
        errorDetail = errorMessage
      }
    }

    const error = new Error(errorDetail)
    error.status = response.status
    error.statusText = response.statusText

    // Add specific handling for common errors
    if (response.status === 401) {
      // Clear invalid token
      localStorage.removeItem('token')
      // You might want to redirect to login here
      error.type = 'AUTHENTICATION_ERROR'
    } else if (response.status === 403) {
      error.type = 'AUTHORIZATION_ERROR'
    } else if (response.status === 404) {
      error.type = 'NOT_FOUND'
    } else if (response.status >= 500) {
      error.type = 'SERVER_ERROR'
    }

    throw error
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    return {}
  }

  try {
    return await response.json()
  } catch (e) {
    console.warn('Failed to parse JSON response:', e)
    return {}
  }
}

// Helper function to add date range parameters
const addDateRangeParams = (params) => {
  // Check if date range context is available
  try {
    // We'll import this at the top of the file to avoid circular dependencies
    const dateRangeContext = window.__CORTEX_DATE_RANGE__
    if (dateRangeContext) {
      const { start_date, end_date } = dateRangeContext
      if (start_date) params.append('date_from', start_date)
      if (end_date) params.append('date_to', end_date)
    }
  } catch (e) {
    // Fail silently if date range context not available
  }
  return params
}

// Helper function to add global filter parameters (Account Manager, Project Manager, Customer)
const addGlobalFilterParams = (params) => {
  try {
    const globalFilters = window.__CORTEX_GLOBAL_FILTERS__
    if (globalFilters) {
      if (globalFilters.account_manager_id) params.append('account_manager_id', globalFilters.account_manager_id)
      if (globalFilters.project_manager_id) params.append('project_manager_id', globalFilters.project_manager_id)
      if (globalFilters.sales_code_id) params.append('sales_code_id', globalFilters.sales_code_id)
    }
  } catch (e) {
    // Fail silently if global filters not available
  }
  return params
}

// Request interceptor for logging and modification
const logRequest = (url, options = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('API Request:', {
      url,
      method: options.method || 'GET',
      headers: options.headers
    })
  }
}

// Metrics Service
export const metricsService = {
  // Get profitability metrics at specified drill-down level
  async getProfitability({ level = 'company', project_id = null, job_id = null }) {
    let params = new URLSearchParams({ level })
    if (project_id) params.append('project_id', project_id)
    if (job_id && level === 'job') params.append('project_id', job_id) // job_id is actually project_id for job level

    // Add global date range parameters
    params = addDateRangeParams(params)
    params = addGlobalFilterParams(params)

    const url = `${API_BASE_URL}/metrics/profitability?${params}`
    logRequest(url, { headers: getAuthHeaders() })

    try {
      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await handleResponse(response)

      // Transform data to match frontend expectations
      return transformProfitabilityData(data, level)
    } catch (error) {
      console.error('Failed to fetch profitability:', error)
      // Return safe default data structure
      return {
        level,
        data: [],
        summary: {
          total_revenue: 0,
          total_cost: 0,
          gross_profit: 0,
          gross_margin_pct: 0,
          labor_cost: 0,
          material_cost: 0,
          overhead_cost: 0
        },
        error: error.message
      }
    }
  },

  // Get efficiency metrics for work centers
  async getEfficiency(workCenter = null, dateFrom = null, dateTo = null) {
    const params = new URLSearchParams()
    if (workCenter) params.append('work_center_id', workCenter)
    if (dateFrom) params.append('date_from', dateFrom)
    if (dateTo) params.append('date_to', dateTo)

    const url = `${API_BASE_URL}/metrics/efficiency?${params}`
    logRequest(url, { headers: getAuthHeaders() })

    try {
      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await handleResponse(response)

      // Ensure data structure matches expectations
      return {
        ...data,
        data: data.data || [],
        summary: data.summary || {
          avg_oee: 0,
          avg_availability: 0,
          avg_performance: 0,
          avg_quality: 0
        }
      }
    } catch (error) {
      console.error('Failed to fetch efficiency:', error)
      return {
        data: [],
        summary: {
          avg_oee: 0,
          avg_availability: 0,
          avg_performance: 0,
          avg_quality: 0
        },
        error: error.message
      }
    }
  },

  // Get cost breakdown for a specific entity
  async getCostBreakdown(entityType, entityId) {
    let params = new URLSearchParams()
    params = addDateRangeParams(params)
    params = addGlobalFilterParams(params)
    const url = `${API_BASE_URL}/metrics/cost-breakdown/${entityType}/${entityId}${params.toString() ? '?' + params.toString() : ''}`
    const response = await fetch(url, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  // Get trend data for metrics
  async getTrends(metric, period = '30d') {
    const params = new URLSearchParams({ metric, period })
    const response = await fetch(`${API_BASE_URL}/metrics/trends?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  }
}

// Dashboard Service
export const dashboardService = {
  // Get dashboard configuration
  async getDashboard(dashboardId = 'default') {
    const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  // Save dashboard configuration
  async saveDashboard(dashboardId, config) {
    const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(config)
    })
    return handleResponse(response)
  },

  // Get widget data
  async getWidgetData(widgetType, params = {}) {
    let queryParams = new URLSearchParams(params)
    queryParams = addDateRangeParams(queryParams)
    queryParams = addGlobalFilterParams(queryParams)
    const response = await fetch(`${API_BASE_URL}/dashboards/widgets/${widgetType}?${queryParams}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  }
}

// Jobs Service
export const jobsService = {
  // Get list of individual jobs with performance metrics
  async listJobs({
    page = 1,
    pageSize = 50,
    search = null,
    jobType = null,
    status = null,
    projectId = null,
    sales_code_id = null,
    draughtsman_name = null,
    sortBy = 'actual_cost',
    sortOrder = 'desc'
  } = {}) {
    try {
      let params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      })

      if (search) params.append('search', search)
      if (jobType) params.append('job_type', jobType)
      if (status) params.append('status', status)
      if (projectId) params.append('project_id', projectId)
      if (sales_code_id) params.append('sales_code_id', sales_code_id)
      if (draughtsman_name) params.append('draughtsman_name', draughtsman_name)

      // Add date range parameters
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const url = `${API_BASE_URL}/jobs/list?${params.toString()}`
      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch jobs list:', error)
      throw error
    }
  },

  // Export all jobs matching filters to CSV (backend-generated, no pagination)
  async exportJobs({ search = null, status = null, sales_code_id = null } = {}) {
    let params = new URLSearchParams()
    if (search) params.append('search', search)
    if (status) params.append('status', status)
    if (sales_code_id) params.append('sales_code_id', sales_code_id)
    params = addDateRangeParams(params)
    params = addGlobalFilterParams(params)

    const response = await fetch(`${API_BASE_URL}/jobs/export?${params}`, {
      method: 'GET',
      headers: getAuthHeaders()
    })

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `jobs_export_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
  },

  // Get job dashboard with PROJECT vs STOCK breakdown
  async getJobDashboard() {
    try {
      // Add date range parameters
      let params = new URLSearchParams()
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)
      const url = `${API_BASE_URL}/jobs/dashboard${params.toString() ? '?' + params.toString() : ''}`

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch job dashboard:', error)
      throw error
    }
  },

  // Get detailed summary for a specific job type
  async getJobTypeSummary(jobType, limit = 20) {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/jobs/summary/${jobType}?limit=${limit}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch ${jobType} job summary:`, error)
      throw error
    }
  },

  // Get detailed information for a specific job
  async getJobDetail(jobId) {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/jobs/${jobId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch job ${jobId} details:`, error)
      throw error
    }
  },

  // Export single job detail to Excel
  async exportJob(jobId) {
    const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/export`, {
      method: 'GET',
      headers: getAuthHeaders()
    })
    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `job_${jobId}_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
  },

  // Get jobs for a specific stock category
  async getStockCategoryJobs(category, limit = 50) {
    try {
      let params = new URLSearchParams({
        limit: limit.toString()
      })

      // Add date range parameters
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const url = `${API_BASE_URL}/jobs/stock/${encodeURIComponent(category)}?${params.toString()}`
      console.log('Stock category API URL:', url)

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch stock category ${category} jobs:`, error)
      throw error
    }
  },

  // Get sector breakdown analysis
  async getSectorBreakdown(period = '30days') {
    try {
      let params = new URLSearchParams()
      params.append('period', period)
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const response = await fetchWithRetry(`${API_BASE_URL}/jobs/sectors?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch sector breakdown:', error)
      throw error
    }
  },

  // Get cost composition analysis
  async getCostComposition(jobType = 'all', period = '30days') {
    try {
      let params = new URLSearchParams()
      params.append('job_type', jobType)
      params.append('period', period)
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const response = await fetchWithRetry(`${API_BASE_URL}/jobs/cost-composition?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch cost composition:', error)
      throw error
    }
  },

  // Get job trends over time
  async getJobTrends(period = '12months', groupBy = 'month') {
    try {
      let params = new URLSearchParams()
      params.append('period', period)
      params.append('group_by', groupBy)
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const response = await fetchWithRetry(`${API_BASE_URL}/jobs/trends?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch job trends:', error)
      throw error
    }
  },

  // Get top customers by revenue
  async getTopCustomers(limit = 10, period = '30days') {
    try {
      let params = new URLSearchParams()
      params.append('limit', limit)
      params.append('period', period)
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const response = await fetchWithRetry(`${API_BASE_URL}/jobs/top-customers?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch top customers:', error)
      throw error
    }
  },

  // Get profitability metrics by sector
  async getSectorProfitability(sector = null, period = '30days') {
    try {
      let params = new URLSearchParams()
      if (sector) params.append('sector', sector)
      params.append('period', period)
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const response = await fetchWithRetry(`${API_BASE_URL}/jobs/profitability-by-sector?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch sector profitability:', error)
      throw error
    }
  },

  // Get list of sales codes for dropdown filter (dynamic counts)
  async getSalesCodes({ status = null, draughtsman_name = null } = {}) {
    try {
      let params = new URLSearchParams()
      if (status) params.append('status', status)
      if (draughtsman_name) params.append('draughtsman_name', draughtsman_name)
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const url = `${API_BASE_URL}/jobs/sales-codes/list${params.toString() ? '?' + params.toString() : ''}`
      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch sales codes:', error)
      throw error
    }
  },

  async getDraughtsmen({ status = null, sales_code_id = null } = {}) {
    try {
      let params = new URLSearchParams()
      if (status) params.append('status', status)
      if (sales_code_id) params.append('sales_code_id', sales_code_id)
      params = addDateRangeParams(params)
      params = addGlobalFilterParams(params)

      const url = `${API_BASE_URL}/jobs/draughtsmen/list${params.toString() ? '?' + params.toString() : ''}`
      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch draughtsmen:', error)
      throw error
    }
  }
}

// Analytics Service - Sector-based business intelligence
export const analyticsService = {
  // Get overview of all sectors
  async getSectorsOverview(dateRange) {
    try {
      const params = new URLSearchParams()
      if (dateRange?.start_date) params.append('start_date', dateRange.start_date)
      if (dateRange?.end_date) params.append('end_date', dateRange.end_date)

      const url = params.toString()
        ? `${API_BASE_URL}/analytics/sectors/overview?${params.toString()}`
        : `${API_BASE_URL}/analytics/sectors/overview`

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch sectors overview:', error)
      throw error
    }
  },

  // Get cross-sector comparison
  async getSectorsComparison(dateRange) {
    try {
      const params = new URLSearchParams()
      if (dateRange?.start_date) params.append('start_date', dateRange.start_date)
      if (dateRange?.end_date) params.append('end_date', dateRange.end_date)

      const url = params.toString()
        ? `${API_BASE_URL}/analytics/sectors/comparison?${params.toString()}`
        : `${API_BASE_URL}/analytics/sectors/comparison`

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error('Failed to fetch sectors comparison:', error)
      throw error
    }
  },

  // Get details for a specific sector
  async getSectorDetails(sector, dateRange) {
    try {
      const params = new URLSearchParams()
      if (dateRange?.start_date) params.append('start_date', dateRange.start_date)
      if (dateRange?.end_date) params.append('end_date', dateRange.end_date)

      const url = params.toString()
        ? `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/overview?${params.toString()}`
        : `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/overview`

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch sector ${sector} details:`, error)
      throw error
    }
  },

  // Get cost composition for a sector
  async getSectorCostComposition(sector, dateRange) {
    try {
      const params = new URLSearchParams()
      if (dateRange?.start_date) params.append('start_date', dateRange.start_date)
      if (dateRange?.end_date) params.append('end_date', dateRange.end_date)

      const url = params.toString()
        ? `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/cost-composition?${params.toString()}`
        : `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/cost-composition`

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch cost composition for sector ${sector}:`, error)
      throw error
    }
  },

  // Get trends for a sector
  async getSectorTrends(sector, startDate = null, endDate = null, granularity = 'monthly') {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      params.append('granularity', granularity)

      const response = await fetchWithRetry(
        `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/trends?${params.toString()}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      )
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch trends for sector ${sector}:`, error)
      throw error
    }
  },

  // Get top customers for a sector
  async getSectorTopCustomers(sector, limit = 10, dateRange) {
    try {
      const params = new URLSearchParams()
      params.append('limit', limit)
      if (dateRange?.start_date) params.append('start_date', dateRange.start_date)
      if (dateRange?.end_date) params.append('end_date', dateRange.end_date)

      const response = await fetchWithRetry(
        `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/top-customers?${params.toString()}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      )
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch top customers for sector ${sector}:`, error)
      throw error
    }
  },

  // Get efficiency metrics for a sector
  async getSectorEfficiency(sector, dateRange) {
    try {
      const params = new URLSearchParams()
      if (dateRange?.start_date) params.append('start_date', dateRange.start_date)
      if (dateRange?.end_date) params.append('end_date', dateRange.end_date)

      const url = params.toString()
        ? `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/efficiency?${params.toString()}`
        : `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/efficiency`

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch efficiency metrics for sector ${sector}:`, error)
      throw error
    }
  },

  // Get profitability tier distribution for a sector
  async getSectorProfitabilityTiers(sector, dateRange) {
    try {
      const params = new URLSearchParams()
      if (dateRange?.start_date) params.append('start_date', dateRange.start_date)
      if (dateRange?.end_date) params.append('end_date', dateRange.end_date)

      const url = params.toString()
        ? `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/profitability-tiers?${params.toString()}`
        : `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/profitability-tiers`

      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch profitability tiers for sector ${sector}:`, error)
      throw error
    }
  },

  // Get recent activity for a sector
  async getSectorRecentActivity(sector, days = 30, limit = 20) {
    try {
      const params = new URLSearchParams()
      params.append('days', days)
      params.append('limit', limit)

      const response = await fetchWithRetry(
        `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/recent-activity?${params.toString()}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      )
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch recent activity for sector ${sector}:`, error)
      throw error
    }
  },

  // Get material costs analysis for a sector
  async getSectorMaterialCosts(sector) {
    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/analytics/sectors/${encodeURIComponent(sector)}/materials`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      )
      return await handleResponse(response)
    } catch (error) {
      console.error(`Failed to fetch material costs for sector ${sector}:`, error)
      throw error
    }
  }
}

// Insights Service (AI-powered analytics)
export const insightsService = {
  // Get AI-generated insights
  async getInsights(level, entityId = null) {
    const params = new URLSearchParams({ level })
    if (entityId) params.append('entity_id', entityId)

    const response = await fetch(`${API_BASE_URL}/insights?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  // Get anomaly detection results
  async getAnomalies(metric, threshold = 0.95) {
    const params = new URLSearchParams({ metric, threshold })
    const response = await fetch(`${API_BASE_URL}/insights/anomalies?${params}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  },

  // Get predictions
  async getPredictions(entityType, entityId, horizon = '30d') {
    const response = await fetch(`${API_BASE_URL}/insights/predictions/${entityType}/${entityId}?horizon=${horizon}`, {
      headers: getAuthHeaders()
    })
    return handleResponse(response)
  }
}

// Export Service
export const exportService = {
  // Export data to Excel
  async exportToExcel(data, filename = 'export.xlsx') {
    const response = await fetch(`${API_BASE_URL}/export/excel`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ data, filename })
    })

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
    return response.ok
  },

  // Export data to PDF
  async exportToPDF(data, filename = 'report.pdf') {
    const response = await fetch(`${API_BASE_URL}/export/pdf`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ data, filename })
    })

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
    return response.ok
  }
}

// Auth Service (supplementary to AuthContext)
export const authService = {
  // Verify token / Get current user
  async verifyToken(token) {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    return handleResponse(response)
  },

  // Refresh token
  async refreshToken(refreshToken) {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    })
    return handleResponse(response)
  }
}

// Data transformation functions to ensure consistent structure
function transformProfitabilityData(data, level) {
  if (!data) return { level, data: [], summary: {} }

  // Ensure summary has all expected fields
  const summary = {
    total_revenue: 0,
    total_cost: 0,
    gross_profit: 0,
    gross_margin_pct: 0,
    labor_cost: 0,
    material_cost: 0,
    overhead_cost: 0,
    ...data.summary
  }

  // Calculate missing cost breakdown if we have total cost
  if (summary.total_cost > 0 && !summary.labor_cost && !summary.material_cost) {
    // Estimate cost breakdown (these would come from actual data in production)
    summary.labor_cost = summary.total_actual_cost * 0.4 || summary.total_cost * 0.4
    summary.material_cost = summary.total_actual_cost * 0.35 || summary.total_cost * 0.35
    summary.overhead_cost = summary.total_actual_cost * 0.25 || summary.total_cost * 0.25
  }

  // Transform data items based on level
  const transformedData = (data.data || []).map(item => {
    if (level === 'company') {
      return {
        ...item,
        name: item.company_name || 'Therion Manufacturing',
        gross_profit: item.total_profit || item.gross_profit || 0,
        total_cost: item.total_cost || 0
      }
    } else if (level === 'project') {
      return {
        ...item,
        name: item.project_name || item.project_id || 'Unknown Project',
        job_description: item.project_name || item.project_id,
        gross_profit: item.gross_profit || 0,
        total_cost: item.total_cost || (item.revenue - item.gross_profit) || 0
      }
    } else if (level === 'job') {
      return {
        ...item,
        name: item.job_description || item.job_id || 'Unknown Job',
        job_description: item.job_description || item.job_id,
        gross_profit: item.gross_profit || 0,
        total_cost: item.total_actual_cost || item.total_cost || 0
      }
    } else {
      return {
        ...item,
        name: item.operation_name || item.operation_id || 'Unknown Operation',
        gross_profit: item.gross_profit || 0,
        total_cost: item.total_cost || 0
      }
    }
  })

  return {
    ...data,
    level,
    data: transformedData,
    summary
  }
}

export default {
  metricsService,
  dashboardService,
  jobsService,
  insightsService,
  exportService,
  authService
}

// Also export api object for convenient access
export const api = {
  baseURL: API_BASE_URL,
  getHeaders: getAuthHeaders,
  ...metricsService,
  ...dashboardService,
  ...jobsService,
  ...insightsService,
  ...exportService,
  ...authService,
  getJobDashboard: jobsService.getJobDashboard,
  getJobTypeSummary: jobsService.getJobTypeSummary,
  getJobDetail: jobsService.getJobDetail,
  getStockCategoryJobs: jobsService.getStockCategoryJobs
}